import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { backofficeClient } from "@/lib/api/backoffice-client";
import type {
  CreateImportJobRequest,
  ExportUrlResponse,
  ImportJobListResponse,
  ImportJobResponse,
  ImportJobStatus,
  ImportProvider,
  ImportTicketListResponse,
  TicketResultStatus,
  UploadUrlResponse,
} from "@/lib/types/itsm-import";

export interface CredentialSummary {
  id: string;
  tenant_id: string;
  name: string;
  provider: string;
  category: string;
  label: string | null;
  created_at: string;
  updated_at: string;
}

interface CredentialsListResponse {
  credentials: CredentialSummary[];
  total: number;
}

const IMPORTS_PATH = "/proxy/itsm-data-importer/api/v1/imports";

const ACTIVE: ImportJobStatus[] = ["pending", "fetching", "processing"];

const CREDENTIAL_PROVIDER_TO_IMPORT_PROVIDER: Partial<Record<string, ImportProvider>> = {
  jsm: "jsm",
  jira: "jsm",
  freshservice: "freshservice",
  servicenow: "servicenow",
  "servicenow-serviceaccount": "servicenow-serviceaccount",
  solarwinds: "solarwinds",
  zendesk: "zendesk",
  monday: "monday",
  sysaid: "sysaid",
};

export function useTenantCredentials(tenantId: string) {
  return useQuery({
    queryKey: ["integrations", "credentials", tenantId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<CredentialsListResponse>(
        "/proxy/integrations/api/v1/credentials",
        { params: { tenant_id: tenantId } },
      );
      return data.credentials.filter(
        (cred) => cred.provider in CREDENTIAL_PROVIDER_TO_IMPORT_PROVIDER,
      );
    },
    enabled: Boolean(tenantId),
  });
}

export function getAvailableProviders(credentials: CredentialSummary[]): ImportProvider[] {
  const providers = new Set<ImportProvider>();
  for (const cred of credentials) {
    const mapped = CREDENTIAL_PROVIDER_TO_IMPORT_PROVIDER[cred.provider];
    if (mapped) {
      providers.add(mapped);
    }
  }
  return Array.from(providers);
}

export function getCredentialsForProvider(credentials: CredentialSummary[], provider: ImportProvider): CredentialSummary[] {
  return credentials.filter((cred) => {
    const mapped = CREDENTIAL_PROVIDER_TO_IMPORT_PROVIDER[cred.provider];
    return mapped === provider;
  });
}

export function useImportJobs(
  options: { tenantId?: string; status?: ImportJobStatus; page?: number; pageSize?: number } = {},
) {
  const { tenantId, status, page = 1, pageSize = 50 } = options;
  return useQuery({
    queryKey: ["itsm-import", "jobs", tenantId, status, page, pageSize],
    queryFn: async () => {
      const { data } = await backofficeClient.get<ImportJobListResponse>(
        IMPORTS_PATH,
        {
          params: {
            ...(tenantId && { tenant_id: tenantId }),
            ...(status && { status }),
            page,
            page_size: pageSize,
          },
        },
      );
      return data;
    },
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs;
      if (jobs?.some((j) => ACTIVE.includes(j.status))) return 5000;
      return false;
    },
  });
}

export function useImportJob(tenantId: string, jobId: string | null) {
  return useQuery({
    queryKey: ["itsm-import", "job", tenantId, jobId],
    queryFn: async () => {
      const { data } = await backofficeClient.get<ImportJobResponse>(
        `${IMPORTS_PATH}/${jobId}`,
        { params: { tenant_id: tenantId } },
      );
      return data;
    },
    enabled: Boolean(tenantId) && Boolean(jobId),
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      if (status && ACTIVE.includes(status)) return 3000;
      return false;
    },
  });
}

export function useCreateImportJob(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: CreateImportJobRequest) => {
      const { data } = await backofficeClient.post<ImportJobResponse>(
        `${IMPORTS_PATH}?tenant_id=${tenantId}`,
        request,
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["itsm-import", "jobs", tenantId],
      });
    },
  });
}

export function useCancelImportJob(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      const { data } = await backofficeClient.post<ImportJobResponse>(
        `${IMPORTS_PATH}/${jobId}/cancel?tenant_id=${tenantId}`,
        undefined,
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["itsm-import", "jobs", tenantId],
      });
    },
  });
}

export function useRetryImportJob(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      const { data } = await backofficeClient.post<ImportJobResponse>(
        `${IMPORTS_PATH}/${jobId}/retry?tenant_id=${tenantId}`,
        undefined,
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["itsm-import", "jobs", tenantId],
      });
    },
  });
}

export function useCloneImportJob(tenantId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (jobId: string) => {
      const { data } = await backofficeClient.post<ImportJobResponse>(
        `${IMPORTS_PATH}/${jobId}/clone?tenant_id=${tenantId}`,
        undefined,
      );
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["itsm-import", "jobs"],
      });
    },
  });
}

/** Fetched on click rather than on load: the presigned URL expires. */
export function useImportExportUrl(tenantId: string) {
  return useMutation({
    mutationFn: async (jobId: string) => {
      const { data } = await backofficeClient.get<ExportUrlResponse>(
        `${IMPORTS_PATH}/${jobId}/export-url`,
        { params: { tenant_id: tenantId } },
      );
      return data;
    },
  });
}

function proxyUploadUrl(url: string): string {
  if (!import.meta.env.DEV) return url;
  try {
    const parsed = new URL(url);
    if (parsed.hostname === "localhost" || parsed.hostname === "127.0.0.1") {
      return `/s3-upload${parsed.pathname}${parsed.search}`;
    }
  } catch {
    // not a valid URL, return as-is
  }
  return url;
}

export function useUploadFile(tenantId: string) {
  return useMutation({
    mutationFn: async (file: File) => {
      const { data: urlData } = await backofficeClient.post<UploadUrlResponse>(
        `${IMPORTS_PATH}/upload-url?tenant_id=${tenantId}`,
        { filename: file.name },
      );

      await axios.put(proxyUploadUrl(urlData.upload_url), file, {
        headers: { "Content-Type": "application/octet-stream" },
      });

      return urlData.s3_key;
    },
  });
}

export function useImportJobTickets(
  tenantId: string,
  jobId: string | null,
  status: TicketResultStatus | undefined,
  page: number,
  pageSize = 50,
  isJobActive = false,
) {
  return useQuery({
    queryKey: ["itsm-import", "tickets", tenantId, jobId, status, page, pageSize],
    queryFn: async () => {
      const { data } = await backofficeClient.get<ImportTicketListResponse>(
        `${IMPORTS_PATH}/${jobId}/tickets`,
        {
          params: {
            tenant_id: tenantId,
            ...(status && { status }),
            page,
            page_size: pageSize,
          },
        },
      );
      return data;
    },
    enabled: Boolean(tenantId) && Boolean(jobId),
    refetchInterval: isJobActive ? 5000 : false,
  });
}

export async function fetchAllImportJobTickets(
  tenantId: string,
  jobId: string,
  status: TicketResultStatus | undefined,
): Promise<ImportTicketListResponse["tickets"]> {
  const allTickets: ImportTicketListResponse["tickets"] = [];
  let page = 1;
  let hasMore = true;
  const pageSize = 200;

  while (hasMore) {
    const { data } = await backofficeClient.get<ImportTicketListResponse>(
      `${IMPORTS_PATH}/${jobId}/tickets`,
      {
        params: {
          tenant_id: tenantId,
          ...(status && { status }),
          page,
          page_size: pageSize,
        },
      },
    );
    allTickets.push(...data.tickets);
    hasMore = allTickets.length < data.total;
    page++;
  }

  return allTickets;
}
